import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, SquareIcon, PaperclipIcon, CpuIcon, FlaskConicalIcon, ZapIcon, BotIcon } from 'lucide-react';
import { useSessionStore, runtimeFromSession } from '../../stores/sessionStore';
import { useModelStore } from '../../stores/modelStore';
import { Badge, Button } from '../common/ui';
import { MessageBubble } from './MessageItems';
const QUICK_PROMPTS = ['检查服务器状态', '分析最近的错误日志', '总结要点'];
export function ChatView({
  sessionId
}: {
  sessionId: string;
}) {
  const models = useModelStore(s => s.models);
  const sessions = useSessionStore(s => s.sessions);
  const messagesMap = useSessionStore(s => s.messages);
  const sendMessage = useSessionStore(s => s.sendMessage);
  const interrupt = useSessionStore(s => s.interrupt);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const session = sessions.find(s => s.sessionId === sessionId);
  const messages = messagesMap[sessionId] || [];
  const running = session?.status === 'running';
  const runtime = session ? runtimeFromSession(session) : null;
  const model = models.find(m => m.modelId === session?.modelId);
  const isTemporary = session?.sessionType === 'temporary';
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);
  const handleSend = () => {
    if (!input.trim() || !session || !runtime || running) return;
    sendMessage(sessionId, runtime, input.trim());
    setInput('');
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  if (!session || !runtime) {
    return <div className="flex h-full items-center justify-center text-sm text-slate-500" data-id="element-75">
        会话不存在或已被删除
      </div>;
  }
  const hasBindings = runtime.skills.length > 0 || runtime.knowledgeBases.length > 0;
  return <div className="mx-auto flex h-full w-full max-w-4xl flex-col" data-id="element-76">
      {/* Header */}
      <div className="border-b border-slate-800 px-1 pb-3" data-id="element-77">
        <div className="flex items-center gap-2" data-id="element-78">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-800" data-id="element-79">
            {isTemporary ? <ZapIcon className="h-4 w-4 text-blue-400" data-id="element-80" /> : <BotIcon className="h-4 w-4 text-indigo-400" data-id="element-81" />}
          </div>
          <h2 className="text-base font-semibold text-slate-100" data-id="element-82">
            {session.title}
          </h2>
          {isTemporary && <Badge tone="blue" data-id="element-83">
              <ZapIcon className="h-3 w-3" data-id="element-84" /> 临时会话
            </Badge>}
          {session.isTest && <Badge tone="amber" data-id="element-85">
              <FlaskConicalIcon className="h-3 w-3" data-id="element-86" /> 测试模式
            </Badge>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-id="element-87">
          <Badge tone="indigo" data-id="element-88">
            <CpuIcon className="h-3 w-3" data-id="element-89" />
            {model?.name || '未指定模型'}
          </Badge>
          {runtime.skills.map(sk => <Badge key={sk.skillId} tone="purple" data-id="element-90">
              {sk.name}
            </Badge>)}
          {runtime.knowledgeBases.map(kb => <Badge key={kb.knowledgeBaseId} tone="green" data-id="element-91">
              {kb.name}
            </Badge>)}
          {!hasBindings && <span className="text-xs text-slate-500" data-id="element-92">
              未绑定 Skills / 知识库
            </span>}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-1 py-4" data-id="element-93">
        {messages.length === 0 && <div className="flex h-full flex-col items-center justify-center text-center text-slate-500" data-id="element-94">
            <p className="text-sm" data-id="element-95">
              输入指令开始
              {isTemporary ? '临时对话' : `与 ${session.title} 对话`}
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2" data-id="element-96">
              {QUICK_PROMPTS.map(q => <button key={q} onClick={() => setInput(q)} className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800" data-id="element-97">
                  {q}
                </button>)}
            </div>
          </div>}
        {messages.map(m => <MessageBubble key={m.id} message={m} data-id="element-98" />)}
      </div>

      {/* Runtime status bar */}
      <div className="flex items-center gap-2 border-t border-slate-800 px-1 py-1.5 text-xs" data-id="element-99">
        <span className={`h-2 w-2 rounded-full ${running ? 'animate-pulse bg-emerald-400' : session.status === 'error' ? 'bg-red-400' : 'bg-slate-500'}`} data-id="element-100" />
        <span className="text-slate-400" data-id="element-101">
          状态：
          {running ? '运行中' : session.status === 'interrupted' ? '已中断' : session.status === 'error' ? '错误' : '空闲'}
        </span>
      </div>

      {/* Input bar */}
      <div className="pt-3" data-id="element-102">
        <div className="flex items-end gap-2 rounded-xl border border-slate-700 bg-slate-800/60 p-2" data-id="element-103">
          <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-700" aria-label="上传文件" data-id="element-104">
            <PaperclipIcon className="h-4 w-4" data-id="element-105" />
          </button>
          <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1} placeholder="输入指令… (Enter 发送，Shift+Enter 换行)" className="max-h-32 flex-1 resize-none bg-transparent py-1 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" data-id="element-106" />
          {running ? <Button variant="danger" size="sm" onClick={() => interrupt(sessionId)} data-id="element-107">
              <SquareIcon className="h-3.5 w-3.5" data-id="element-108" /> 中断
            </Button> : <Button size="sm" onClick={handleSend} disabled={!input.trim()} data-id="element-109">
              <SendIcon className="h-3.5 w-3.5" data-id="element-110" /> 发送
            </Button>}
        </div>
      </div>
    </div>;
}